const box = "rounded-sm border px-3 py-2 text-center font-mono text-[11px] font-medium leading-tight sm:text-xs";
const arrow = "flex items-center justify-center text-brand-slate";

function Arrow({ label }) {
  return (
    <div className={arrow} aria-hidden="true">
      <svg width="40" height="16" viewBox="0 0 40 16" className="flex-shrink-0">
        <line x1="0" y1="8" x2="32" y2="8" stroke="currentColor" strokeWidth="1.5" />
        <polyline points="26,3 33,8 26,13" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      {label && <span className="sr-only">{label}</span>}
    </div>
  );
}

export function BluetoothFlow() {
  const detectors = ["Smartphone", "Access point", "IoT infrastructure", "Vehicle telematics"];
  return (
    <figure className="rounded-sm border border-border bg-brand-surface p-5" data-testid="diagram-bluetooth-flow">
      <figcaption className="mb-4 font-mono text-xs uppercase tracking-widest text-brand-slate">
        GNTS1 — partner detection network flow
      </figcaption>
      <div className="space-y-2">
        <div className="flex flex-wrap items-stretch gap-2 sm:flex-nowrap">
          <div className={`${box} flex-1 border-cyan-600/60 bg-white text-brand-navy`}>
            GNTS1 Smart Sticker
            <span className="block text-[10px] font-normal text-brand-slate">secure, encrypted Bluetooth signal — no GPS, no SIM, no modem</span>
          </div>
          <Arrow label="Secure encrypted Bluetooth signal" />
          <div className={`${box} flex-[1.4] border-cyan-600/60 bg-white text-brand-navy`}>
            Compatible partner detector
            <span className="mt-1.5 flex flex-wrap justify-center gap-1">
              {detectors.map((d) => (
                <span key={d} className="rounded-full border border-cyan-600/40 bg-cyan-50 px-2 py-0.5 text-[9px] font-normal text-cyan-900">{d}</span>
              ))}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-stretch gap-2 sm:flex-nowrap">
          <div className={`${box} flex-1 border-primary/60 bg-white text-brand-navy`}>
            Secure cloud relay
            <span className="block text-[10px] font-normal text-brand-slate">detection + available detector location</span>
          </div>
          <Arrow label="Secure relay to cloud" />
          <div className={`${box} flex-[1.4] border-primary/60 bg-white text-brand-navy`}>
            GoAndTrack
            <span className="block text-[10px] font-normal text-brand-slate">shipment visibility and operational workflows</span>
          </div>
        </div>
      </div>
      <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-600/50 bg-white px-3 py-1.5 font-mono text-[11px] font-semibold text-cyan-900" data-testid="diagram-network-badge">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />
        100M+ participating devices and rapidly expanding
      </p>
      <p className="mt-3 text-xs leading-relaxed text-brand-slate">
        The partner detector is a necessary part of the architecture — the sticker never connects to a satellite or cellular tower itself.
      </p>
    </figure>
  );
}

export function CellularFlow() {
  return (
    <figure className="rounded-sm border border-border bg-brand-surface p-5" data-testid="diagram-cellular-flow">
      <figcaption className="mb-4 font-mono text-xs uppercase tracking-widest text-brand-slate">
        LL100 — cellular-enabled flow
      </figcaption>
      <div className="flex flex-wrap items-stretch gap-2 sm:flex-nowrap">
        <div className={`${box} border-lime-600/60 bg-white text-brand-navy`}>
          Location inputs
          <span className="block text-[10px] font-normal text-brand-slate">GNSS · Wi-Fi · Bluetooth · network-assisted</span>
        </div>
        <Arrow label="Best available positioning input" />
        <div className={`${box} border-lime-600/60 bg-white text-brand-navy`}>
          LL100 label
          <span className="block text-[10px] font-normal text-brand-slate">integrated cellular connectivity (eSIM)</span>
        </div>
        <Arrow label="Data sent over mobile network" />
        <div className={`${box} border-primary/60 bg-white text-brand-navy`}>
          Mobile network
          <span className="block text-[10px] font-normal text-brand-slate">no participating detector required</span>
        </div>
        <Arrow label="Delivered to platform" />
        <div className={`${box} border-primary/60 bg-white text-brand-navy`}>GoAndTrack → your dashboard</div>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-brand-slate">
        The label reports through its own cellular connection — a more autonomous architecture. Positioning accuracy varies with the method available; cellular-network location is not universally GNSS-equivalent.
      </p>
    </figure>
  );
}

export function PilotWorkflow() {
  const steps = ["Define", "Configure", "Run", "Review"];
  return (
    <figure className="rounded-sm border border-border bg-brand-surface p-5" data-testid="diagram-pilot-workflow">
      <figcaption className="mb-4 font-mono text-xs uppercase tracking-widest text-brand-slate">
        Pilot programme workflow
      </figcaption>
      <ol className="flex flex-wrap items-stretch gap-2 sm:flex-nowrap">
        {steps.map((s, i) => (
          <li key={s} className="flex flex-1 items-center gap-2">
            <div className={`${box} w-full border-primary/50 bg-white text-brand-navy`}>
              <span className="mb-1 block text-[10px] font-semibold text-primary">0{i + 1}</span>
              {s}
            </div>
            {i < steps.length - 1 && <Arrow label={`${s} to ${steps[i + 1]}`} />}
          </li>
        ))}
      </ol>
    </figure>
  );
}

export function DecisionGraphic() {
  return (
    <figure className="rounded-sm border border-border bg-white p-5" data-testid="diagram-decision">
      <figcaption className="mb-4 font-mono text-xs uppercase tracking-widest text-brand-slate">
        The one question that matters most
      </figcaption>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-sm border border-cyan-600/40 bg-cyan-50/50 p-4">
          <p className="text-sm font-semibold text-brand-navy">Do shipments move through lanes with everyday device activity?</p>
          <p className="mt-1 text-xs leading-relaxed text-brand-slate">
            Cities, hubs and busy transport routes → <strong>GNTS1</strong> rides a 100M+ partner detection network at the lowest per-unit cost.
          </p>
        </div>
        <div className="rounded-sm border border-lime-600/40 bg-lime-50/50 p-4">
          <p className="text-sm font-semibold text-brand-navy">Do goods travel remote or unaccompanied, anywhere?</p>
          <p className="mt-1 text-xs leading-relaxed text-brand-slate">
            Sparse routes and higher-risk freight → <strong>LL100</strong> reports through its own cellular connection.
          </p>
        </div>
      </div>
    </figure>
  );
}
